import PropTypes, { instanceOf } from 'prop-types';
import { useState, useEffect, useMemo } from 'react';
import React from 'react';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import {
    Avatar,
    Box,
    Button,
    Chip,
    Divider,
    Grid,
    Typography,
    ListItem,
    IconButton,
    Collapse,
    ListItemText,
    List,
    Modal,
    RadioGroup,
    FormControlLabel,
    Radio,
    TextField,
    Paper,
    Stack
} from '@mui/material';
import { Link } from 'react-router-dom';
import PhotoCameraBackIcon from '@mui/icons-material/PhotoCameraBack';
import EventSchedule from '../EventForm/Schedule';
import { selectAllPeopleNamesAndTitles } from '../../../../api/peopleApiSlice';
import ImageSlider from './ImageSlider';
import ProgressLoader from '../../../../components/ProgressLoader';
import useDocumentTitle from '../../../../hooks/useDocumentTitle';
import { selectCurrentUser } from '../../../authentication/authSlice';
import { roles } from '../../../../config/roles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EventDesc from './EventDesc';
import { useEditEventMutation } from '../../../../api/eventsApiSlice';
import TilaisuusHenkilotList from './TilaisuusHenkilotList';
import Kuvaus from './Kuvaus';
import ShowReport from './showReport';
import { useGetReportQuery } from '../../../../api/reportsApiSlice';
import HelpIconWithTooltip from '../../../../components/HelpIconWithTooltip';
import ReassignEventOwner from './ReassignEventOwner';

const ViewEvent = ({ event }) => {
    const currentUser = useSelector(selectCurrentUser);
    const [laskunumero, setLaskunumero] = useState(null);

    useDocumentTitle(event?.nimi);

    const isNotAuthorized =
        event.status === 'luonnos' &&
        currentUser?.userId !== event.user.id &&
        currentUser.role !== 'Admin' &&
        currentUser.role !== 'Muokkaaja';

    const allPeopleNamesAndTitles = useSelector(selectAllPeopleNamesAndTitles);
    const [editEvent] = useEditEventMutation();
    const [userInvolvement, setUserInvolvement] = useState({
        henkilo: currentUser?.name || '',
        rooli: '',
        julkinen: 'Kyllä',
    });

    const [showLisatiedot, setShowLisatiedot] = useState(false);

    const toggleLisatiedot = () => {
        setShowLisatiedot(!showLisatiedot);
    };

    const [isMukanaOlevatOpen, setIsMukanaOlevatOpen] = useState(false);

    const toggleMukanaOlevat = () => {
        setIsMukanaOlevatOpen(!isMukanaOlevatOpen);
    };

    const reportId = event.raportti || null;

    const {
        data: report,
        isLoading,
        isError
    } = useGetReportQuery(
        { id: reportId },
        { skip: !reportId }
    );

    const [collapseOpen, setCollapseOpen] = useState([]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState('');

    const [isRaporttiModalOpen, setIsRaporttiModalOpen] = useState(false);
    const [isLaskutusModalOpen, setIsLaskutusModalOpen] = useState(false);

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const openRaporttiModal = () => {
        setIsRaporttiModalOpen(true);
    };

    const openLaskutusModal = () => {
        setIsLaskutusModalOpen(true);
    };

    const closeLaskutusModal = () => {
        setIsLaskutusModalOpen(false);
    };

    const closeRaporttiModal = () => {
        setIsRaporttiModalOpen(false);
    };

    const handleOptionChange = (event) => {
        setSelectedOption(event.target.value);
    };

    const handleIlmoittauduClick = () => {
        openModal();
    };

    const handleRaporttiOpenClick = () => {
        openRaporttiModal();
    };

    const handleLaskutusOpenClick = () => {
        openLaskutusModal();
    };

    // Memoize ohjelma to prevent unnecessary re-renders
    const ohjelma = useMemo(() => event?.ohjelma || [], [event?.ohjelma]);

    // Initialize collapseOpen state based on event.ohjelma structure
    useEffect(() => {
        if (!ohjelma || !Array.isArray(ohjelma)) {
            setCollapseOpen([]);
            return;
        }
        const newCollapseOpen = ohjelma.map((day) =>
            Array(day?.tilaisuudet?.length || 0).fill(false)
        );
        setCollapseOpen(newCollapseOpen);
    }, [ohjelma]);

    const toggleCollapse = (ohjelmaIndex, tilaisuusIndex) => {
        setCollapseOpen((prevCollapseOpen) => {
            // Create a deep copy of prevCollapseOpen
            const updatedCollapseOpen = prevCollapseOpen.map((day) => [...(day || [])]);

            // Ensure the ohjelmaIndex exists
            if (!updatedCollapseOpen[ohjelmaIndex]) {
                updatedCollapseOpen[ohjelmaIndex] = Array(
                    ohjelma?.[ohjelmaIndex]?.tilaisuudet?.length || 0
                ).fill(false);
            }

            // Toggle the specific tilaisuusIndex
            updatedCollapseOpen[ohjelmaIndex][tilaisuusIndex] =
                !updatedCollapseOpen[ohjelmaIndex][tilaisuusIndex];

            return updatedCollapseOpen;
        });
    };

    const eventHasNotEnded = dayjs(event.lopetus, 'DD.MM.YYYY HH:mm').isAfter(dayjs());
    const userAuthorized = (
        currentUser?.userId === event?.user?.id ||
        [roles.admin, roles.muokkaaja, roles.laskuttaja].includes(currentUser.role)
    );

    const canEdit = userAuthorized && (eventHasNotEnded || currentUser?.role === roles.admin);
    const canReport = userAuthorized && !eventHasNotEnded;

    const reportingLink = event?.raportti
        ? `/tapahtumat/raportit/muokkaa/${event.id}`
        : `/tapahtumat/raportit/${event.id}`;

    const images = event?.kuvat;
    const bannerImage = images?.find(img => img.bannerImage === true);

    const eventPageStyles = {
        margin: 'auto',
        width: { xs: '94%', md: '88%', lg: '82%' },
        px: 0,
        py: { xs: 3, md: 5 },
    };

    const imageStyles = {
        height: { xs: 220, md: 280 },
        width: '100%',
        maxWidth: { xs: 420, md: 340 },
        objectFit: 'cover',
        borderRadius: 4,
    };

    const heroCardStyles = {
        p: { xs: 2, md: 3 },
        borderRadius: 4,
        border: '1px solid',
        borderColor: 'grey.200',
        boxShadow: '0 12px 28px rgba(15, 23, 42, 0.08)',
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fbfc 100%)'
    };

    const sectionCardStyles = {
        p: { xs: 2, md: 3 },
        borderRadius: 4,
        border: '1px solid',
        borderColor: 'grey.200',
        boxShadow: '0 8px 22px rgba(15, 23, 42, 0.06)',
        backgroundColor: 'white',
    };

    const sectionHeadingStyles = {
        fontFamily: '"Bree Serif", serif',
        fontSize: { xs: '1.25rem', md: '1.45rem' },
        mb: 2,
        color: 'text.primary'
    };

    const metaLabelStyles = {
        fontWeight: 700,
        color: 'text.secondary',
        minWidth: { xs: '100%', sm: 120 },
        letterSpacing: '0.01em'
    };

    const metaValueStyles = {
        fontWeight: 600,
        color: 'text.primary'
    };

    const renderMetaRow = (label, value) => (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 0.25, sm: 2 }}
            sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'grey.100' }}
        >
            <Typography sx={metaLabelStyles}>{label}</Typography>
            <Typography sx={metaValueStyles}>{value}</Typography>
        </Stack>
    );

    const handleEditEvent = async (updatedEvent) => {
        await editEvent(updatedEvent)
            .then((response) => {
                // Handle success if needed
            })
            .catch((error) => {
                console.error('Error updating event:', error);
            });
    };

    const getUserRegisteredRole = () => {
        if (!event || !event.mukanaolevat || !currentUser) {
            return '';
        }

        const userRoles = ['Ehtoollisavustaja', 'Esirukouspalvelu'];

        for (const role of userRoles) {
            const userInvolvement = event.mukanaolevat.find(
                (involvement) => involvement.henkilo === currentUser.name && involvement.rooli === role
            );
            if (userInvolvement) {
                return role;
            }
        }

        return '';
    };

    const isUserAlreadyRegistered = () => {
        return event.mukanaolevat.some((involvement) => {
            return involvement.henkilo === currentUser.name &&
                   ['Ehtoollisavustaja', 'Esirukouspalvelu'].includes(involvement.rooli);
        });
    };

    useEffect(() => {
        if (userInvolvement.rooli) {
            const updatedEvent = {
                ...event,
                mukanaolevat: [...event.mukanaolevat, userInvolvement],
            };
            handleEditEvent(updatedEvent);
        }
    }, [userInvolvement, event, editEvent]);

    const handleHyvaksyClick = () => {
        setUserInvolvement((prevUserInvolvement) => ({
            ...prevUserInvolvement,
            rooli: selectedOption,
        }));
        closeModal();
        setTimeout(() => {
            window.location.reload();
        }, 500);
    };

    const handleSendToLaskutus = () => {
        const updatedEvent = {
            ...event,
            status: 'laskutuksessa',
        };
        handleEditEvent(updatedEvent);
        setTimeout(() => {
            window.location.reload();
        }, 500);
    };

    const handleLaskuta = (laskunumero) => {
        const updatedEvent = {
            ...event,
            status: 'laskutettu',
            laskunumero: laskunumero
        };
        handleEditEvent(updatedEvent);
        setTimeout(() => {
            window.location.reload();
        }, 500);
    };

    const handleCancelRegistration = () => {
        if (!event || !currentUser) {
            closeModal();
            return;
        }

        const userRoles = ['Ehtoollisavustaja', 'Esirukouspalvelu'];
        const updatedMukanaolevat = event.mukanaolevat.filter((involvement) => {
            return !(involvement.henkilo === currentUser.name && userRoles.includes(involvement.rooli));
        });

        const updatedEvent = {
            ...event,
            mukanaolevat: updatedMukanaolevat,
        };
        handleEditEvent(updatedEvent);
        closeModal();
        setTimeout(() => {
            window.location.reload();
        }, 500);
    };

    const calculateTotalKansanopistotunnit = (event) => {
        let totalKansanopistotunnit = 0;

        event?.ohjelma?.forEach((day) => {
            day?.tilaisuudet?.forEach((tilaisuus) => {
                if (Array.isArray(tilaisuus?.kansanopistotunnit) && tilaisuus.kansanopistotunnit.length > 0) {
                    totalKansanopistotunnit += tilaisuus.kansanopistotunnit[0]?.kansanopistotunnit || 0;
                }
            });
        });

        return totalKansanopistotunnit;
    };

    const totalKansanopistotunnit = calculateTotalKansanopistotunnit(event);

    if (!currentUser || !event)
        return <ProgressLoader msg='Ladataan tietoja...' />;

    if (event.message === 'No events were found with this ID.') {
        return (
            <Box sx={{ textAlign: 'center', padding: '50px' }}>
                <Typography variant="h5" color="error">
                    Access Denied
                </Typography>
                <Typography variant="body1">
                    Tätä tapahtumaa ei löytynyt.
                </Typography>
            </Box>
        );
    }

    if (isNotAuthorized) {
        return (
            <Box sx={{ textAlign: 'center', padding: '50px' }}>
                <Typography variant="h5" color="error">
                    Access Denied
                </Typography>
                <Typography variant="body1">
                    Sinulla ei ole oikeutta nähdä tätä tapahtumaa.
                    Ota yhteyttä tapahtuman tekijään {event?.user?.name} saadaksesi lisätietoja.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={eventPageStyles}>
            <Stack spacing={3.5}>
                <Paper sx={heroCardStyles}>
                    <Grid container spacing={{ xs: 2.5, md: 4 }} alignItems='stretch'>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'stretch', height: '100%' }}>
                                {bannerImage ? (
                                    <Box component='img' alt='event image' src={bannerImage?.src} sx={imageStyles} />
                                ) : (
                                    <Avatar
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: 'grey.100',
                                            ...imageStyles
                                        }}
                                    >
                                        <img src='/SROS-logo-vihrea.jpg' style={{ width: '70%', objectFit: 'contain' }} />
                                    </Avatar>
                                )}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Stack spacing={2.5}>
                                <Box>
                                    <Typography
                                        component='h1'
                                        variant='h2'
                                        sx={{
                                            fontFamily: '"Bree Serif", serif',
                                            lineHeight: 1.15,
                                            fontSize: {
                                                xs: '2rem',
                                                sm: '2.5rem',
                                                md: '3rem',
                                                lg: '3.3rem',
                                            },
                                            mb: 1,
                                        }}
                                    >
                                        {event.nimi}
                                    </Typography>
                                    <Typography variant='body1' sx={{ fontSize: { xs: '1rem', md: '1.08rem' }, color: 'text.secondary', maxWidth: 780 }}>
                                        {event?.paikka?.nimi}{event?.paikka?.nimi && event?.paikka?.kunta ? ', ' : ''}{event?.paikka?.kunta}
                                    </Typography>
                                </Box>

                                <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap' alignItems='center'>
                                    <Chip
                                        label={event.status}
                                        color={
                                            event.status === 'julkaistu' ? 'success' :
                                                ['laskutuksessa', 'laskutettu'].includes(event.status) ? 'warning' : 'primary'
                                        }
                                        sx={{ fontWeight: 700 }}
                                    />
                                    {event.luonteet?.slice(0, 3).map((luonne) => (
                                        <Chip key={luonne} label={luonne} variant='outlined' sx={{ fontWeight: 600 }} />
                                    ))}
                                </Stack>

                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.25, sm: 2 }}>
                                    <Box sx={{ minWidth: { sm: 220 } }}>
                                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: 'text.secondary' }}>Alkaa</Typography>
                                        <Typography sx={{ fontSize: { xs: '1.1rem', md: '1.2rem' }, fontWeight: 700 }}>{event.aloitus}</Typography>
                                    </Box>
                                    <Box sx={{ minWidth: { sm: 220 } }}>
                                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: 'text.secondary' }}>Päättyy</Typography>
                                        <Typography sx={{ fontSize: { xs: '1.1rem', md: '1.2rem' }, fontWeight: 700 }}>{event.lopetus}</Typography>
                                    </Box>
                                </Stack>

                                <Box>
                                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>Paikka</Typography>
                                    <Typography sx={{ fontSize: { xs: '1.05rem', md: '1.15rem' }, fontWeight: 600 }}>{event?.paikka?.nimi}</Typography>
                                    <Typography sx={{ fontSize: '1rem' }}>{event.paikka?.osoite}</Typography>
                                    <Typography sx={{ fontSize: '1rem' }}>{event.paikka?.postinumero} {event.paikka?.postitoimipaikka}</Typography>
                                    <Typography sx={{ fontSize: '1rem' }}>{event.paikka?.maakunta} {event.paikka?.kunta}</Typography>
                                    <Typography sx={{ fontSize: '1rem' }}>{event.paikka?.keskus === 'Ystävämatkat' && event.paikka?.kuvaus}</Typography>
                                </Box>

                                <Stack direction='row' spacing={1.25} useFlexGap flexWrap='wrap' alignItems='center'>
                                    <Button
                                        disabled={!canEdit}
                                        onClick={() => sessionStorage.setItem('copiedSessionValues', null)}
                                        component={Link}
                                        to={`/tapahtumat/muokkaa/${event.id}`}
                                        variant='contained'
                                        sx={{
                                            borderRadius: 3,
                                            backgroundColor: 'taphaPurple.main',
                                            px: 2.25,
                                            py: 1.1,
                                            fontWeight: 700,
                                            '&:hover': { backgroundColor: 'taphaPurple.dark' }
                                        }}
                                    >
                                        Muokkaa
                                    </Button>
                                    <Button
                                        disabled={!canReport}
                                        component={Link}
                                        to={reportingLink}
                                        variant='contained'
                                        sx={{
                                            borderRadius: 3,
                                            whiteSpace: 'nowrap',
                                            px: 2.25,
                                            py: 1.1,
                                            fontWeight: 700,
                                            backgroundColor: 'taphaPurple.main',
                                            '&:hover': { backgroundColor: 'taphaPurple.dark' }
                                        }}
                                    >
                                        {event?.raportti ? 'Muokkaa raporttia' : 'Raportoi'}
                                    </Button>
                                    {event?.luonteet.includes('Jumalanpalvelus') && (
                                        <Button
                                            variant='contained'
                                            onClick={handleIlmoittauduClick}
                                            sx={{ borderRadius: 3, whiteSpace: 'nowrap', px: 2.25, py: 1.1, fontWeight: 700 }}
                                        >
                                            Avustustehtävät
                                        </Button>
                                    )}
                                </Stack>

                                {event.raportti && (
                                    <Stack direction='row' spacing={1.25} useFlexGap flexWrap='wrap'>
                                        <Button
                                            variant='contained'
                                            onClick={handleRaporttiOpenClick}
                                            sx={{ py: 1, borderRadius: 3, whiteSpace: 'nowrap', fontWeight: 700 }}
                                        >
                                            Näytä raportti
                                        </Button>
                                        <Button
                                            variant='contained'
                                            onClick={handleSendToLaskutus}
                                            disabled={event.status === 'laskutuksessa' || event.status === 'laskutettu'}
                                            sx={{
                                                backgroundColor: 'green',
                                                py: 1,
                                                borderRadius: 3,
                                                whiteSpace: 'nowrap',
                                                fontWeight: 700,
                                                '&:hover': { backgroundColor: 'green' },
                                            }}
                                        >
                                            {event.status === 'laskutuksessa' ? 'Laskutuksessa' : event.status === 'laskutettu' ? 'Laskutettu' : 'Lähetä laskutukseen'}
                                        </Button>
                                        {currentUser.role === 'Laskuttaja' && event.status === 'laskutuksessa' && (
                                            <Button
                                                variant='contained'
                                                onClick={handleLaskutusOpenClick}
                                                disabled={event.status !== 'laskutuksessa'}
                                                sx={{ backgroundColor: 'green', py: 1, borderRadius: 3, whiteSpace: 'nowrap', fontWeight: 700, '&:hover': { backgroundColor: 'green' } }}
                                            >
                                                {event.status === 'laskutuksessa' ? 'Laskuta' : 'Laskutettu'}
                                            </Button>
                                        )}
                                    </Stack>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <Stack spacing={3}>
                            <Paper sx={sectionCardStyles}>
                                <Typography variant='h5' component='h3' sx={sectionHeadingStyles}>Perustiedot</Typography>
                                <Stack spacing={0.25}>
                                    {renderMetaRow('Tapahtuman tila', event.status)}
                                    {renderMetaRow('Tapahtuman id', event?.id)}
                                    {renderMetaRow('Omistaja', event?.user?.name)}
                                    {event.kansanopistokurssi === true && renderMetaRow('Tyyppi', `Kansanopistokurssi, kokonaistunnit ${totalKansanopistotunnit}`)}
                                </Stack>
                            </Paper>

                            <Paper sx={sectionCardStyles}>
                                <Typography variant='h5' component='h3' sx={sectionHeadingStyles}>Luokitukset</Typography>
                                <Typography sx={{ fontWeight: 700, color: 'text.secondary', mb: 1 }}>Luonteet</Typography>
                                <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap' sx={{ mb: 2.5 }}>
                                    {event.luonteet?.map((luonne) => <Chip key={luonne} label={luonne} />)}
                                </Stack>
                                <Typography sx={{ fontWeight: 700, color: 'text.secondary', mb: 1 }}>Kohdeyleisöt</Typography>
                                <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap'>
                                    {event.kohdeyleiso?.map((yleiso) => <Chip key={yleiso} label={yleiso} variant='outlined' />)}
                                </Stack>
                            </Paper>

                            {(event.vastuuhenkilot?.length > 0 || event.mukanaolevat?.length > 0) && (
                                <Paper sx={sectionCardStyles}>
                                    <Typography variant='h5' component='h3' sx={sectionHeadingStyles}>Ihmiset</Typography>
                                    {event.vastuuhenkilot && event.vastuuhenkilot.length > 0 && (
                                        <>
                                            <Typography sx={{ fontWeight: 700, color: 'text.secondary', mb: 1 }}>Vastuuhenkilöt</Typography>
                                            <List sx={{ py: 0 }}>
                                                {event.vastuuhenkilot.map((involvement, index) => (
                                                    <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                                        <ListItemText primary={involvement.henkilo} primaryTypographyProps={{ fontWeight: 600 }} />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </>
                                    )}
                                    {event.mukanaolevat && event.mukanaolevat.length > 0 && (
                                        <>
                                            <Button variant='contained' size='small' sx={{ mt: 1, mb: 1.5 }} onClick={toggleMukanaOlevat}>
                                                {isMukanaOlevatOpen ? 'Sulje mukana olevat' : 'Näytä mukana olevat'}
                                            </Button>
                                            {isMukanaOlevatOpen && (
                                                <List sx={{ py: 0 }}>
                                                    {event.mukanaolevat.map((involvement, index) => (
                                                        <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                                            <ListItemText
                                                                primary={involvement.henkilo}
                                                                secondary={involvement.rooli}
                                                                primaryTypographyProps={{ fontWeight: 600 }}
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            )}
                                        </>
                                    )}
                                </Paper>
                            )}

                            <Paper sx={sectionCardStyles}>
                                <Typography variant='h5' component='h3' sx={sectionHeadingStyles}>Linkit ja hallinta</Typography>
                                {event.lyytilinkit?.ilmoittautumislinkki?.length > 0 && (
                                    <Box sx={{ mb: 2.5 }}>
                                        <Typography sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.75 }}>Ilmoittautumislinkki</Typography>
                                        <Typography>
                                            <a href={event.lyytilinkit.ilmoittautumislinkki}>Ilmoittaudu</a>
                                        </Typography>
                                    </Box>
                                )}
                                <Box sx={{ mb: 2.5 }}>
                                    <Typography sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.75 }}>Linkki tapahtumaan</Typography>
                                    <a
                                        href={`https://sro.fi/tapahtuma/?${event.id}`}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        style={{ color: '#1a73e8', textDecoration: 'none', fontWeight: '500', fontSize: '18px' }}
                                    >
                                        {event?.nimi}
                                    </a>
                                </Box>

                                {currentUser.role === roles.admin && (
                                    <ReassignEventOwner
                                        event={event}
                                        onSuccess={() => {
                                            window.location.reload();
                                        }}
                                    />
                                )}
                            </Paper>

                            {event.lisatiedot && event.lisatiedot.replace(/<[^>]*>?/gm, '').trim().length > 0 && (
                                <Paper sx={sectionCardStyles}>
                                    <Stack direction='row' alignItems='center' justifyContent='space-between' spacing={2}>
                                        <Typography variant='h5' component='h3' sx={{ ...sectionHeadingStyles, mb: 0 }}>Lisätietoja</Typography>
                                        <Button variant='outlined' size='small' sx={{ color: 'text.secondary' }} onClick={toggleLisatiedot}>
                                            {showLisatiedot ? 'Piilota' : 'Näytä'}
                                        </Button>
                                    </Stack>
                                    {showLisatiedot && (
                                        <Grid sx={{ mt: 2 }}>
                                            <EventDesc eventDesc={event.lisatiedot} />
                                        </Grid>
                                    )}
                                </Paper>
                            )}
                        </Stack>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <Stack spacing={3}>
                            <Paper sx={sectionCardStyles}>
                                <Typography variant='h5' component='h3' sx={sectionHeadingStyles}>Tapahtuman kuvaus</Typography>
                                <Grid>
                                    <Kuvaus marginLeft={0} eventDesc={event.kuvaus} />
                                </Grid>
                            </Paper>

                            <Paper sx={sectionCardStyles}>
                                <Typography variant='h5' component='h3' sx={sectionHeadingStyles}>Tapahtuman ohjelma</Typography>
                                <Stack spacing={2.5}>
                                    {ohjelma?.length > 0 ? (
                                        ohjelma.map((ohjelmanumero, ohjelmaIndex) => (
                                            <Box
                                                key={ohjelmaIndex}
                                                sx={{
                                                    border: '1px solid',
                                                    borderColor: 'grey.200',
                                                    borderRadius: 3,
                                                    backgroundColor: 'grey.50',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        px: { xs: 1.5, md: 2 },
                                                        py: 1.25,
                                                        backgroundColor: '#eef6f7',
                                                        borderBottom: '1px solid',
                                                        borderColor: 'grey.200',
                                                    }}
                                                >
                                                    <Typography variant='h6' sx={{ fontWeight: 700, color: 'taphaBlue.main' }}>
                                                        {ohjelmanumero?.tilaisuudet?.length > 0 ? ohjelmanumero?.pvm : ''}
                                                    </Typography>
                                                </Box>

                                                <Stack spacing={1.25} sx={{ p: { xs: 1.25, md: 1.5 } }}>
                                                    {ohjelmanumero?.tilaisuudet?.map((tilaisuus, tilaisuusIndex) => {
                                                        const isOpen = collapseOpen[ohjelmaIndex]?.[tilaisuusIndex] || false;
                                                        const timeLabel = `${tilaisuus.aloitus}${tilaisuus.lopetus !== 'Invalid Date' ? ` - ${tilaisuus.lopetus}` : ''}`;
                                                        const hasDescription = Boolean(tilaisuus.kuvaus && tilaisuus.kuvaus.replace(/<[^>]*>?/gm, '').trim().length > 0);
                                                        const hasPeople = tilaisuus.henkilot.length > 0;
                                                        const kansanopistoTunnit = Array.isArray(tilaisuus?.kansanopistotunnit) && tilaisuus.kansanopistotunnit.length > 0
                                                            ? tilaisuus.kansanopistotunnit[0]?.kansanopistotunnit || 0
                                                            : null;

                                                        return (
                                                            <Box
                                                                key={tilaisuusIndex}
                                                                sx={{
                                                                    border: '1px solid',
                                                                    borderColor: isOpen ? 'taphaGreen.main' : 'grey.200',
                                                                    borderRadius: 3,
                                                                    backgroundColor: 'white',
                                                                    boxShadow: isOpen ? '0 8px 20px rgba(15, 23, 42, 0.08)' : 'none',
                                                                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: { xs: 'flex-start', sm: 'center' },
                                                                        gap: 1.5,
                                                                        px: { xs: 1.25, md: 1.5 },
                                                                        py: 1.25,
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            minWidth: { xs: 114, sm: 132 },
                                                                            px: 1.25,
                                                                            py: 0.9,
                                                                            borderRadius: 2,
                                                                            backgroundColor: '#f2f8fb',
                                                                            border: '1px solid',
                                                                            borderColor: '#d7e7ef',
                                                                        }}
                                                                    >
                                                                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                                            Aika
                                                                        </Typography>
                                                                        <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.94rem', md: '1rem' }, whiteSpace: 'nowrap' }}>
                                                                            {timeLabel}
                                                                        </Typography>
                                                                    </Box>

                                                                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                                        <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.05rem', md: '1.12rem' }, lineHeight: 1.35 }}>
                                                                            {tilaisuus.nimi}
                                                                        </Typography>
                                                                        <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap' sx={{ mt: 0.75 }}>
                                                                            {kansanopistoTunnit !== null && (
                                                                                <Chip
                                                                                    label={`Kansanopistotunteja ${kansanopistoTunnit}`}
                                                                                    size='small'
                                                                                    variant='outlined'
                                                                                />
                                                                            )}
                                                                            {hasDescription && (
                                                                                <Chip label='Kuvaus saatavilla' size='small' variant='outlined' />
                                                                            )}
                                                                            {hasPeople && (
                                                                                <Chip label={`Henkilöitä ${tilaisuus.henkilot.length}`} size='small' variant='outlined' />
                                                                            )}
                                                                        </Stack>
                                                                    </Box>

                                                                    <IconButton
                                                                        disableRipple
                                                                        onClick={() => toggleCollapse(ohjelmaIndex, tilaisuusIndex)}
                                                                        sx={{ mt: { xs: 0.25, sm: 0 }, alignSelf: { xs: 'flex-start', sm: 'center' } }}
                                                                    >
                                                                        {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                                    </IconButton>
                                                                </Box>

                                                                <Collapse sx={{ width: '100%' }} unmountOnExit in={isOpen} timeout='auto'>
                                                                    <Box sx={{ px: { xs: 1.25, md: 1.5 }, pb: 1.5, pt: 0.25 }}>
                                                                        {(hasDescription || hasPeople) && <Divider sx={{ mb: 1.5 }} />}
                                                                        {hasDescription && (
                                                                            <Box sx={{ mb: hasPeople ? 2 : 0 }}>
                                                                                <Typography sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.75 }}>
                                                                                    Tilaisuuden kuvaus
                                                                                </Typography>
                                                                                <EventDesc marginLeft={0} eventDesc={tilaisuus.kuvaus} />
                                                                            </Box>
                                                                        )}
                                                                        {hasPeople && (
                                                                            <Box>
                                                                                <Typography sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.75 }}>
                                                                                    Tilaisuuteen liittyvät henkilöt
                                                                                </Typography>
                                                                                <TilaisuusHenkilotList
                                                                                    attendingPeople={tilaisuus.henkilot}
                                                                                    findTitles={allPeopleNamesAndTitles}
                                                                                />
                                                                            </Box>
                                                                        )}
                                                                    </Box>
                                                                </Collapse>
                                                            </Box>
                                                        );
                                                    })}
                                                </Stack>
                                            </Box>
                                        ))
                                    ) : (
                                        <Typography>Ei ohjelmaa määritetty</Typography>
                                    )}
                                </Stack>
                            </Paper>

                            <Paper sx={sectionCardStyles}>
                                <Typography variant='h5' component='h3' sx={{ ...sectionHeadingStyles, textAlign: 'center' }}>
                                    {images?.length ? 'Tapahtuman kuvat' : 'Tapahtumalla ei vielä ole kuvia'}
                                </Typography>
                                {images?.length ? <ImageSlider images={images} /> : null}
                            </Paper>
                        </Stack>
                    </Grid>
                </Grid>
            </Stack>
            <Modal
                open={isModalOpen}
                onClose={closeModal}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        width: 400,
                        bgcolor: 'background.paper',
                        border: '2px solid #000',
                        boxShadow: 24,
                        p: 2,
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                    }}
                >
                    {isUserAlreadyRegistered() ? (
                        <div>
                            <Typography variant="h6" id="modal-title">
                                Olet jo ilmoittautunut tehtävään: {getUserRegisteredRole()}
                            </Typography>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Button variant="contained" onClick={closeModal}>
                                    Sulje
                                </Button>
                                <Button variant="contained" onClick={handleCancelRegistration}>
                                    Peru ilmoittautuminen
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <Typography variant="h6" id="modal-title">
                                Valitse avustustehtävä
                            </Typography>
                            <RadioGroup
                                value={selectedOption}
                                onChange={handleOptionChange}
                            >
                                <FormControlLabel
                                    value="Ehtoollisavustaja"
                                    control={<Radio />}
                                    label="Ehtoollisavustaja"
                                />
                                <FormControlLabel
                                    value="Esirukouspalvelu"
                                    control={<Radio />}
                                    label="Esirukouspalvelu"
                                />
                            </RadioGroup>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Button variant="contained" onClick={closeModal}>
                                    Sulje
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleHyvaksyClick}
                                    sx={{ backgroundColor: 'taphaGreen.main', color: 'white' }}
                                >
                                    Hyväksy
                                </Button>
                            </div>
                        </div>
                    )}
                </Box>
            </Modal>
            <Modal
                open={isRaporttiModalOpen}
                onClose={closeRaporttiModal}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        width: 400,
                        maxHeight: '80vh',
                        bgcolor: 'background.paper',
                        border: '2px solid #000',
                        boxShadow: 24,
                        p: 2,
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        overflowY: 'auto',
                        '@media (min-width: 600px)': {
                            width: 600,
                        },
                        '@media (min-width: 960px)': {
                            width: 800,
                        },
                        '@media (min-width: 1280px)': {
                            width: 1000,
                        },
                    }}
                >
                    {<ShowReport event={event} report={report} />}
                </Box>
            </Modal>
            <Modal
                open={isLaskutusModalOpen}
                onClose={closeLaskutusModal}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        width: 400,
                        maxHeight: '80vh',
                        bgcolor: 'background.paper',
                        border: '2px solid #000',
                        boxShadow: 24,
                        p: 2,
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        overflowY: 'auto',
                        '@media (min-width: 600px)': {
                            width: 600,
                        },
                        '@media (min-width: 960px)': {
                            width: 800,
                        },
                        '@media (min-width: 1280px)': {
                            width: 1000,
                        },
                    }}
                >
                    <h1 id="modal-title">Laskutus</h1>
                    <TextField
                        label="Laskunumero"
                        value={laskunumero}
                        onChange={(e) => setLaskunumero(e.target.value)}
                        fullWidth
                    />
                    <Button
                        variant="contained"
                        sx={{ mt: 2 }}
                        color="primary"
                        onClick={() => handleLaskuta(laskunumero)}
                    >
                        Tallenna laskunumero
                    </Button>
                </Box>
            </Modal>
        </Box>
    );
};

ViewEvent.propTypes = {
    event: PropTypes.object
};

export default ViewEvent;